import { Fragment, useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, monthLabel, useFpContext } from "@/lib/fp";

const cell = (v) => (v === undefined || v === null ? "—" : inr(v));
const sum = (obj) => Object.values(obj || {}).reduce((s, v) => s + v, 0);

/** Grid keys carry the RM / FG split as "Wastage — RM"; the design prints it as a child row. */
const keysFor = (category, sub) => [`${category} — ${sub}`, sub];
const pick = (grid, category, sub) =>
  keysFor(category, sub).find((k) => grid[k] !== undefined);

const Panel = ({ title, subtitle, note, children, footer, testid }) => (
  <div className="panel overflow-hidden" data-testid={testid}>
    <div className="border-b border-border px-6 py-4">
      <p className="label-caps">{subtitle}</p>
      <h3 className="font-display text-lg text-navy">{title}</h3>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
    <div className="overflow-x-auto">{children}</div>
    {footer}
  </div>
);

const Tie = ({ total, reference, referenceLabel, testid }) => {
  if (reference === undefined || reference === null) return null;
  const gap = Math.round((total - reference) * 100) / 100;
  return (
    <p className={`border-t border-border px-6 py-3 text-[11px] ${
      Math.abs(gap) < 1 ? "bg-success/10 text-success" : "bg-warning/10 text-[hsl(30_88%_28%)]"}`}
       data-testid={testid}>
      {Math.abs(gap) < 1
        ? `Ties to ${referenceLabel} exactly.`
        : `This table adds up to ${inr(total)} against ${referenceLabel} of ${inr(reference)} — `
          + `${inr(Math.abs(gap))} is on lines that carry no tag yet.`}
    </p>
  );
};

const Head = ({ cols, first }) => (
  <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
    <tr>
      <th className="sticky left-0 z-10 bg-muted/50 px-4 py-3 text-left">{first}</th>
      {cols.map((c) => <th key={c} className="px-4 py-3 text-right">{c}</th>)}
      <th className="px-4 py-3 text-right">Total</th>
    </tr>
  </thead>
);

/** A Level 3 cost table in the design's own row order: group heading, its child rows, a subtotal
 *  per group and the grand total at the foot. Rows the client has no figure for still print. */
const TreeTable = ({ tree, grid, cols, totalLabel, first, testid }) => {
  const used = new Set();
  const groups = (tree || []).map(({ category, sub_categories }) => {
    const children = (sub_categories || []).map((sub) => {
      const key = pick(grid, category, sub);
      if (key) used.add(key);
      return { label: sub, values: (key && grid[key]) || {} };
    });
    if (grid[category] !== undefined) {
      used.add(category);
      children.push({ label: `${category} (not split)`, values: grid[category] });
    }
    return { category, children };
  });
  const extra = Object.keys(grid || {}).filter((k) => !used.has(k));
  if (extra.length) {
    groups.push({ category: "Other components", outside: true,
                  children: extra.map((k) => ({ label: k, values: grid[k] })) });  }
  const colTotal = (c) => groups.reduce((s, g) => s
    + g.children.reduce((t, ch) => t + (ch.values[c] || 0), 0), 0);
  const grand = cols.reduce((s, c) => s + colTotal(c), 0);

  return (
    <table className="w-full min-w-[720px] text-xs">
      <Head cols={cols} first={first} />
      <tbody className="divide-y divide-border">
        {groups.map((g) => (
          <Fragment key={g.category}>
            <tr className="bg-muted/30"
                data-testid={`${testid}-group-${g.category.replace(/\W+/g, "-")}`}>
              <td className="sticky left-0 z-10 bg-muted/30 px-4 py-2.5 font-semibold text-navy">
                {g.category}
                {g.outside && (
                  <span className="ml-2 text-[9px] font-normal uppercase tracking-[0.1em] text-muted-foreground">
                    outside this table's taxonomy
                  </span>
                )}
              </td>
              {cols.map((c) => (
                <td key={c} className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
                  {cell(g.children.some((ch) => ch.values[c] !== undefined)
                    ? g.children.reduce((t, ch) => t + (ch.values[c] || 0), 0) : undefined)}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
                {inr(g.children.reduce((t, ch) => t + sum(ch.values), 0))}
              </td>
            </tr>
            {g.children.map((ch) => (
              <tr key={`${g.category}-${ch.label}`}
                  data-testid={`${testid}-row-${ch.label.replace(/\W+/g, "-")}`}>
                <td className="sticky left-0 z-10 bg-card px-4 py-2 pl-9 text-foreground/85">
                  {ch.label}
                </td>
                {cols.map((c) => (
                  <td key={c} className="px-4 py-2 text-right font-mono tabular-nums text-navy">
                    {cell(ch.values[c])}
                  </td>
                ))}
                <td className="bg-muted/20 px-4 py-2 text-right font-mono tabular-nums text-navy">
                  {sum(ch.values) ? inr(sum(ch.values)) : "—"}
                </td>
              </tr>
            ))}
          </Fragment>
        ))}
        <tr className="bg-navy/5">
          <td className="sticky left-0 z-10 bg-[hsl(215_37%_96%)] px-4 py-3 font-display text-sm text-navy">
            {totalLabel}
          </td>
          {cols.map((c) => (
            <td key={c} className="px-4 py-3 text-right font-mono font-bold tabular-nums text-navy">
              {inr(colTotal(c))}
            </td>
          ))}
          <td className="px-4 py-3 text-right font-mono font-bold tabular-nums text-navy"
              data-testid={`${testid}-grand`}>
            {inr(grand)}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/** A plain row x column matrix (revenue stream x channel, and the wide fixed cost view). */
const Matrix = ({ grid, first, testid }) => {
  const rows = Object.keys(grid || {});
  const cols = [...new Set(rows.flatMap((r) => Object.keys(grid[r])))];
  const grand = rows.reduce((s, r) => s + sum(grid[r]), 0);
  return (
    <table className="w-full min-w-[720px] text-xs">
      <Head cols={cols} first={first} />
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={r} data-testid={`${testid}-row-${r.replace(/\W+/g, "-")}`}>
            <td className="sticky left-0 z-10 bg-card px-4 py-2.5 text-navy">{r}</td>
            {cols.map((c) => (
              <td key={c} className="px-4 py-2.5 text-right font-mono tabular-nums text-navy">
                {cell(grid[r][c])}
              </td>
            ))}
            <td className="bg-muted/25 px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
              {inr(sum(grid[r]))}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/40">
          <td className="sticky left-0 z-10 bg-[hsl(40_30%_95%)] px-4 py-2.5 font-semibold text-navy">Total</td>
          {cols.map((c) => (
            <td key={c} className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
              {inr(rows.reduce((s, r) => s + (grid[r][c] || 0), 0))}
            </td>
          ))}
          <td className="px-4 py-2.5 text-right font-mono font-bold tabular-nums text-navy">
            {inr(grand)}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/** Revenue by product category — the design's 'Revenue category' sheet. */
const SingleColumn = ({ map, first, totalLabel, testid }) => {
  const keys = Object.keys(map || {});
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
        <tr>
          <th className="px-5 py-3 text-left">{first}</th>
          <th className="px-5 py-3 text-right">Amount</th>
          <th className="px-5 py-3 text-right">Share</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {keys.map((k) => (
          <tr key={k} data-testid={`${testid}-row-${k.replace(/\W+/g, "-")}`}>
            <td className="px-5 py-2.5 text-navy">{k}</td>
            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-navy">{inr(map[k])}</td>
            <td className="px-5 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
              {sum(map) ? `${Math.round((map[k] / sum(map)) * 1000) / 10}%` : "—"}
            </td>
          </tr>
        ))}
        <tr className="bg-muted/40">
          <td className="px-5 py-3 font-semibold text-navy">{totalLabel}</td>
          <td className="px-5 py-3 text-right font-mono font-bold tabular-nums text-navy"
              data-testid={`${testid}-total`}>
            {inr(sum(map))}
          </td>
          <td />
        </tr>
      </tbody>
    </table>
  );
};

const Waiting = ({ children, testid }) => (
  <p className="px-6 py-6 text-sm text-muted-foreground" data-testid={testid}>{children}</p>
);

export default function FpLevel3() {
  const { tenantId, period, basis } = useFpContext();
  const [m, setM] = useState(null);
  const [masters, setMasters] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const [out, ms] = await Promise.all([
        api.get(`${fpBase(tenantId)}/outputs`, { params: { period, basis } }),
        api.get(`${fpBase(tenantId)}/masters`),
      ]);
      setM(out.data);
      setMasters(ms.data);
    } catch (e) { setM({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const l3 = m?.levels?.level3;
  const l3basis = m?.levels?.basis_labels || {};
  const l1 = m?.levels?.level1;
  const colsOf = (grid) => [...new Set(Object.values(grid || {})
    .flatMap((cells) => Object.keys(cells)))];
  const fcTree = masters?.fixed_cost_lines?.tree || [];
  const fcGrid = l3?.fixed_cost_grid || {};
  const fcCols = colsOf(fcGrid);
  // the design prints fixed cost down a single Amount column, grouped by business function
  const fcVertical = fcTree.map(({ category, sub_categories }) => ({
    category,
    sub_categories,
  }));
  const fcByFunction = Object.fromEntries(Object.entries(fcGrid).map(([fn, comps]) =>
    [fn === "Unallocated" ? "Unassigned" : fn, comps]));

  return (
    <FpShell
      title="Level 3 detail"
      description="Level 3 exactly as the Aikyam FP level design lays it out: revenue by channel × stream and by product category, production variable cost by component × product category, distribution variable cost by component × channel, and fixed cost by business function. Every cell comes from the tags approved in Classification review."
    >
      {!period ? (
        <EmptyState testid="fp-level3-empty" title="Nothing uploaded yet"
                    message="Upload a Profit & Loss under Statements first." />
      ) : m?.error ? (
        <EmptyState testid="fp-level3-error" title="Unavailable" message={m.error} />
      ) : !m || !masters ? (
        <p className="label-caps animate-pulse">Building Level 3</p>
      ) : (
        <div className="space-y-6" data-testid="fp-level3">
          <div className="panel p-6">
            <p className="label-caps">
              {m.fiscal_year} · {basis === "ytd" ? "cumulative to" : "month of"} {monthLabel(period)}
            </p>
            <p className="mt-2 text-sm text-foreground/80">
              Every row of the design template is printed, with a dash where this client has no
              figure. Cells come from the tags approved in Classification review; where the books
              carry no tags yet, a table falls back to the interface sheet and says so on its
              header. Anything tagged on one axis only sits under <b>Unallocated</b>, and the tie
              note under each table says how much is still untagged.
            </p>
          </div>

          <Panel testid="fp-l3-revenue" subtitle="Distribution channel × revenue stream"
                 title="Revenue matrix"
                 note={l3.revenue_grid_source === "interface"
                   ? "Built from the sales register for now — the Tally revenue lines carry no channel or stream tags yet, so this view comes from the uploaded sales detail instead of the books."
                   : "Combining stream and channel shows which stream performs through which channel."}
                 footer={<Tie total={Object.values(l3.revenue_grid || {})
                   .reduce((s, c) => s + sum(c), 0)} reference={l1.revenue}
                              referenceLabel="Level 1 revenue" testid="fp-l3-revenue-tie" />}>
            {Object.keys(l3.revenue_grid || {}).length
              ? <Matrix grid={l3.revenue_grid} first="Particulars" testid="fp-l3-revenue" />
              : <Waiting testid="fp-l3-revenue-none">
                  No revenue stream or distribution channel tags yet — set them on the revenue lines
                  in Classification review, or upload a Sales Register.
                </Waiting>}
          </Panel>

          <Panel testid="fp-l3-revenue-category" subtitle="Product category"
                 title="Revenue category"
                 note="Where the revenue comes from, and whether the business leans on a few categories."
                 footer={<Tie total={sum(m.levels.level2.product_category)}
                              reference={l1.revenue} referenceLabel="Level 1 revenue"
                              testid="fp-l3-revenue-category-tie" />}>
            {Object.keys(m.levels.level2.product_category || {}).length
              ? <SingleColumn map={m.levels.level2.product_category} first="Revenue"
                              totalLabel="Total" testid="fp-l3-revenue-category" />
              : <Waiting testid="fp-l3-revenue-category-none">
                  No product category tags on the revenue lines yet.
                </Waiting>}
          </Panel>

          <Panel testid="fp-l3-pvc" subtitle={`Cost component × ${(l3basis.pvc || "product category").toLowerCase()}`}
                 title="Production variable cost"
                 note="Raw material COGS, variable direct expense, stock variance and wastage — closing stock, interunit sale and wastage print as deductions."
                 footer={<Tie total={Object.values(l3.pvc_grid || {})
                   .reduce((s, c) => s + sum(c), 0)} reference={l1.pvc}
                              referenceLabel="Level 1 production variable cost"
                              testid="fp-l3-pvc-tie" />}>
            <TreeTable tree={masters.pvc_components?.tree} grid={l3.pvc_grid || {}}
                       cols={colsOf(l3.pvc_grid).length ? colsOf(l3.pvc_grid) : ["Unallocated"]}
                       first="Particulars" totalLabel="Total Production VC" testid="fp-l3-pvc" />
          </Panel>

          <Panel testid="fp-l3-dvc" subtitle={`Cost component × ${(l3basis.dvc || "distribution channel").toLowerCase()}`}
                 title="Distribution variable cost"
                 note={l3.dvc_grid_source === "interface"
                   ? "Built from the distribution sheet for now — no Tally expense line carries a distribution tag yet, so this view comes from the uploaded distribution detail instead of the books. Suggested tags are waiting in Classification review."
                   : "Discount, packing charges, free samples, stock variance and wastage."}
                 footer={<Tie total={Object.values(l3.dvc_grid || {})
                   .reduce((s, c) => s + sum(c), 0)} reference={l1.dvc}
                              referenceLabel="Level 1 distribution variable cost"
                              testid="fp-l3-dvc-tie" />}>
            <TreeTable tree={masters.dvc_components?.tree} grid={l3.dvc_grid || {}}
                       cols={colsOf(l3.dvc_grid).length ? colsOf(l3.dvc_grid) : ["Unallocated"]}
                       first="Particulars" totalLabel="Total Distribution VC" testid="fp-l3-dvc" />
          </Panel>

          <Panel testid="fp-l3-fixed" subtitle="Business function" title="Fixed cost"
                 note="The design's layout: each business function with its own components beneath it. Anything not yet assigned to a function sits under Unassigned."
                 footer={<Tie total={Object.values(fcGrid).reduce((s, c) => s + sum(c), 0)}
                              reference={l1.fixed_expenses} referenceLabel="Level 1 fixed cost"
                              testid="fp-l3-fixed-tie" />}>
            {Object.keys(fcGrid).length ? (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3 text-left">Particulars</th>
                    <th className="px-5 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[...fcVertical, { category: "Unassigned", sub_categories: [] }].map((g) => {
                    const comps = fcByFunction[g.category] || {};
                    const listed = g.sub_categories.length
                      ? g.sub_categories : Object.keys(comps);
                    const extra = Object.keys(comps).filter((c) => !listed.includes(c));
                    const rowsFor = [...listed, ...extra];
                    if (g.category === "Unassigned" && !rowsFor.length) return null;
                    return (
                      <Fragment key={g.category}>
                        <tr className="bg-muted/30"
                            data-testid={`fp-l3-fixed-group-${g.category.replace(/\W+/g, "-")}`}>
                          <td className="px-5 py-2.5 font-semibold text-navy">{g.category}</td>
                          <td className="px-5 py-2.5 text-right font-mono font-semibold tabular-nums text-navy">
                            {sum(comps) ? inr(sum(comps)) : "—"}
                          </td>
                        </tr>
                        {rowsFor.map((c) => (
                          <tr key={`${g.category}-${c}`}
                              data-testid={`fp-l3-fixed-row-${g.category.replace(/\W+/g, "-")}-${c.replace(/\W+/g, "-")}`}>
                            <td className="px-5 py-2 pl-10 text-foreground/85">{c}</td>
                            <td className="px-5 py-2 text-right font-mono tabular-nums text-navy">
                              {cell(comps[c])}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  <tr className="bg-navy/5">
                    <td className="px-5 py-3 font-display text-sm text-navy">Total Fixed Cost</td>
                    <td className="px-5 py-3 text-right font-mono font-bold tabular-nums text-navy"
                        data-testid="fp-l3-fixed-grand">
                      {inr(Object.values(fcGrid).reduce((s, c) => s + sum(c), 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <Waiting testid="fp-l3-fixed-none">
                No fixed cost tags yet — assign each fixed-cost ledger to a business function in
                Classification review.
              </Waiting>
            )}
          </Panel>

          {fcCols.length > 1 && (
            <Panel testid="fp-l3-fixed-matrix" subtitle="Business function × cost component"
                   title="Fixed cost matrix"
                   note="The same figures side by side, to compare one component across functions.">
              <Matrix grid={fcGrid} first="Particulars" testid="fp-l3-fixed-matrix" />
            </Panel>
          )}
        </div>
      )}
    </FpShell>
  );
}
